import React, { useState } from 'react';
import { connect } from 'react-redux';
import { uniqBy, pipe, map, prop, filter, allPass, reject, isNil, pathOr, equals, pathEq, props, join, toLower, contains } from 'ramda';
import moment from 'moment';
import PagedTable from '../PagedTable';
import { USER_NOTIFICATION_TYPE } from '../../constants';
import RawHTML from '../generic/RawHTML';
import SearchInput from '../SearchInput';

export const AllNotifications = ({ notificationList }) => {

    // Used for tenant filter dropdown
    const tenantList = pipe(
        map(prop('tenant')),
        // Remove where tenant is null
        reject(isNil),
        // Reduce to unique tenants
        uniqBy(prop('id')),
    )(notificationList);

    const [typeFilter, setTypeFilter] = useState('');
    const [tenantFilter, setTenantFilter] = useState('');
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');

    const pageSize = 20;

    const locale = window.navigator.userLanguage || window.navigator.language;
    moment.locale(locale);

    const columns = [
        {
            renderHeader: () => 'Subject',
            renderCell: ({ item }) => <span className="nowrap">{item.subject}</span>,
        },
        {
            renderHeader: () => 'Content',
            renderCell: ({ item }) => <RawHTML HTMLContent={item.content} />,
        },
        {
            renderHeader: () => 'Tenant',
            renderCell: ({ item }) => pathOr('', ['tenant', 'developerName'], item),
        },
        {
            renderHeader: () => 'Created',
            renderCell: ({ item }) => moment(item.createdAt).format('L'),
        },
    ];

    const filteredNotificationList = filter(
        allPass([
            // Release type only applicable to all tenants
            notification => equals(tenantFilter, '') || equals(typeFilter, USER_NOTIFICATION_TYPE.release)
                || pathEq(['tenant', 'id'], tenantFilter, notification),
            notification => equals(typeFilter, '') || pathEq(['type'], typeFilter, notification),
            notification => equals(searchQuery, '')
                || contains(searchQuery.toLowerCase(), toLower(join(' ', props(['subject', 'content'], notification)))),
        ]),
        notificationList,
    );

    const onTypeFilterChange = ({ target }) => {
        setTypeFilter(target.value);
        setPage(1);
    };

    const onTenantFilterChange = ({ target }) => {
        setTenantFilter(target.value);
        setPage(1);
    };

    const onSearchChange = (query) => {
        setSearchQuery(query);
        setPage(1);
    };

    return (
        <span className="admin-transition">
            <div className="flex-column full-height">
                <div className="admin-page">
                    <h1>Notifications</h1>
                    <div className="flex margin-bottom">
                        <label className="control-label">
                            <span className="margin-bottom-small block">Notification Type</span>
                            <select
                                data-component="type-filter"
                                className="form-control form-select"
                                value={typeFilter}
                                onChange={onTypeFilterChange}
                            >
                                <option value="">All</option>
                                <option value={USER_NOTIFICATION_TYPE.release}>Releases</option>
                                <option value={USER_NOTIFICATION_TYPE.fault}>Fault</option>
                                <option value={USER_NOTIFICATION_TYPE.warning}>Warning</option>
                                <option value={USER_NOTIFICATION_TYPE.passwordReset}>Password Reset</option>
                                <option value={USER_NOTIFICATION_TYPE.tenantCreation}>Tenant Creation</option>
                                <option value={USER_NOTIFICATION_TYPE.provisioning}>Provisioning</option>
                                <option value={USER_NOTIFICATION_TYPE.deleteTenant}>Delete Tenant</option>
                                <option value={USER_NOTIFICATION_TYPE.deleteAll}>Delete All</option>
                            </select>
                        </label>
                        <label className="control-label margin-left">
                            <span className="margin-bottom-small block">Tenant</span>
                            <select
                                data-component="tenant-filter"
                                className="form-control form-select"
                                value={tenantFilter}
                                onChange={onTenantFilterChange}
                            >
                                {
                                    <option key="all" value="">All</option>
                                }
                                {
                                    tenantList.map(tenant => <option value={tenant.id} key={tenant.id}>{tenant.developerName}</option>)
                                }
                            </select>
                        </label>
                        <label className="control-label margin-left">
                            <span className="margin-bottom-small block">&nbsp;</span>
                            <SearchInput onSearch={onSearchChange} />
                        </label>
                    </div>
                    <PagedTable
                        className="table table-align-top"
                        columns={columns}
                        items={filteredNotificationList}
                        page={page}
                        pageSize={pageSize}
                        onPageClick={setPage}
                    />
                </div>
            </div>
        </span>
    );
};

const mapStateToProps = ({
    userNotifications: { list: notificationList },
}) => ({ notificationList });

export default connect(mapStateToProps)(AllNotifications);
